import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

const PAGE_FILTER_PARAM = 'pf';
const TAG_SEPARATOR = '::';

/**
 * Encodes the analytics page-tier filters into the `pf` query param, tagged with
 * the surface id so a stale param from another surface is never applied. Only
 * the `pf` param is touched; unrelated params (global filters, tab) are kept.
 */
export function encodePageFilters(surfaceId: string, filters: AnalyticsFilter[], base: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(base);
    if (filters.length === 0) {
        next.delete(PAGE_FILTER_PARAM);
        return next;
    }
    next.set(PAGE_FILTER_PARAM, `${surfaceId}${TAG_SEPARATOR}${JSON.stringify(filters)}`);
    return next;
}

/** Reads page-tier filters from `pf`, but only when the tag matches `surfaceId`. Tolerates malformed values. */
export function decodePageFilters(params: URLSearchParams, surfaceId: string): AnalyticsFilter[] | null {
    const raw = params.get(PAGE_FILTER_PARAM);
    if (raw === null) return null;
    const separatorAt = raw.indexOf(TAG_SEPARATOR);
    if (separatorAt < 0) return null;
    if (raw.slice(0, separatorAt) !== surfaceId) return null;
    const json = raw.slice(separatorAt + TAG_SEPARATOR.length);
    try {
        const parsed = JSON.parse(json) as unknown;
        if (!Array.isArray(parsed)) return null;
        const filters: AnalyticsFilter[] = [];
        for (const entry of parsed) {
            if (typeof entry !== 'object' || entry === null) continue;
            const field = (entry as { field?: unknown }).field;
            const values = (entry as { values?: unknown }).values;
            if (typeof field !== 'string' || !Array.isArray(values)) continue;
            const clean = values.filter((value): value is string => typeof value === 'string');
            if (clean.length > 0) filters.push({ field, values: clean });
        }
        return filters;
    } catch {
        return null;
    }
}
